import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDocs } from "../context/DocsContext";
import { useAuth } from "../context/AuthContext";
import {
  FileText,
  FileSpreadsheet,
  File,
  Trash2,
  Edit2,
  Download,
  Tag,
  PenLine,
  Loader,
  AlertCircle,
  Mail,
  CheckSquare,
  Square,
  Send,
  BarChart2,
  FolderInput,
  X,
  Archive,
  RotateCcw,
} from "lucide-react";
import EditModal from "./EditModal";
import EmailModal from "./EmailModal";
import FormResponsesModal from "./FormResponsesModal";
import { useConfirm } from "./ConfirmDialog";
import { SkeletonGrid } from "./Skeletons";
import { api } from "../api";
import { formatDate } from "../utils/dates";
import { CATEGORIES } from "../constants";

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.96 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 340, damping: 28, mass: 0.8, delay: Math.min(index * 0.055, 0.3) },
  }),
  exit: { opacity: 0, scale: 0.94, y: -4, transition: { duration: 0.18, ease: "easeIn" } },
};

const TYPE_ICON = {
  pdf: <FileText size={28} className="doc-icon pdf" />,
  xlsx: <FileSpreadsheet size={28} className="doc-icon xlsx" />,
  xls: <FileSpreadsheet size={28} className="doc-icon xlsx" />,
  docx: <File size={28} className="doc-icon docx" />,
  doc: <File size={28} className="doc-icon docx" />,
};

const TYPE_COLOR = { pdf: "badge-pdf", xlsx: "badge-xlsx", xls: "badge-xlsx", docx: "badge-docx", doc: "badge-docx" };

function fileExt(doc) {
  return (doc.filename || doc.originalname || "").split(".").pop().toLowerCase();
}

function fmtSize(bytes) {
  if (!bytes) return "-";
  return bytes > 1e6 ? `${(bytes / 1e6).toFixed(1)} MB` : `${(bytes / 1e3).toFixed(0)} KB`;
}

function isComunicado(name) {
  return (name || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().includes("comunicac");
}

export default function DocumentGrid({ category, search, onEdit }) {
  const {
    docs,
    trashDocs,
    loading,
    trashLoading,
    error,
    deleteDoc,
    moveDoc,
    fetchTrash,
    restoreDoc,
    removeDocForever,
    fetchDocs,
  } = useDocs();
  const { canDelete, canMoveDocuments, isAdmin } = useAuth();
  const [editDoc, setEditDoc] = useState(null);
  const [emailDocs, setEmailDocs] = useState(null);
  const [emailComunicado, setEmailComunicado] = useState(null);
  const [responsesDoc, setResponsesDoc] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveCategory, setMoveCategory] = useState("");
  const [moving, setMoving] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [removingForeverId, setRemovingForeverId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const { confirm, ConfirmUI } = useConfirm();

  const filtered = docs.filter((doc) => {
    const matchCategory = category === "Todos" || doc.category === category;
    const matchSearch = doc.name?.toLowerCase().includes(search.toLowerCase()) ||
      doc.description?.toLowerCase().includes(search.toLowerCase());
    const visibleForRole = isAdmin || !isComunicado(doc.name);
    return matchCategory && matchSearch && visibleForRole;
  });

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelected(selected.length === filtered.length ? [] : filtered.map((doc) => doc.id));
  };

  const comunicadoFor = (toSend) => {
    const categories = [...new Set(toSend.map((doc) => doc.category))];
    if (categories.length !== 1) return null;
    return docs.find((doc) => doc.category === categories[0] && isComunicado(doc.name)) || null;
  };

  const sendSelected = () => {
    const toSend = docs.filter((doc) => selected.includes(doc.id));
    if (toSend.length > 0) {
      setEmailDocs(toSend);
      setEmailComunicado(comunicadoFor(toSend));
    }
  };

  const sendOne = (doc) => {
    setEmailDocs([doc]);
    setEmailComunicado(comunicadoFor([doc]));
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected([]);
  };

  const openMoveModal = (doc) => {
    setMoveTarget(doc);
    setMoveCategory(doc.category);
  };

  const closeMoveModal = () => {
    setMoveTarget(null);
    setMoveCategory("");
    setMoving(false);
  };

  const openTrashModal = async () => {
    setTrashOpen(true);
    await fetchTrash();
  };

  const closeTrashModal = () => {
    setTrashOpen(false);
    setRestoringId(null);
    setRemovingForeverId(null);
  };

  const handleMoveDocument = async () => {
    if (!moveTarget || !moveCategory || moveCategory === moveTarget.category) return;
    setMoving(true);
    try {
      const result = await moveDoc(moveTarget.id, moveCategory);
      if (result?.error) throw new Error(result.error);
      closeMoveModal();
    } catch (err) {
      alert(err.message || "No se pudo mover el documento");
      setMoving(false);
    }
  };

  const handleDeleteDocument = async (doc) => {
    const ok = await confirm({
      title: "Enviar a papelera",
      message: `"${doc.name}" se movera a la papelera y se eliminara definitivamente 30 dias despues si no lo restauras.`,
      confirmLabel: "Enviar a papelera",
    });
    if (!ok) return;

    const result = await deleteDoc(doc.id);
    if (result?.error) {
      alert(result.error);
      return;
    }

    setSelected((prev) => prev.filter((id) => id !== doc.id));
  };

  const handleRestoreDocument = async (doc) => {
    setRestoringId(doc.id);
    try {
      const result = await restoreDoc(doc.id);
      if (result?.error) throw new Error(result.error);
    } catch (err) {
      alert(err.message || "No se pudo restaurar el documento");
    } finally {
      setRestoringId(null);
    }
  };

  const handleRemoveForever = async (doc) => {
    const ok = await confirm({
      title: "Eliminar definitivamente",
      message: `"${doc.name}" se eliminara para siempre ahora mismo y no se podra recuperar.`,
      confirmLabel: "Eliminar para siempre",
    });
    if (!ok) return;

    setRemovingForeverId(doc.id);
    try {
      const result = await removeDocForever(doc.id);
      if (result?.error) throw new Error(result.error);
    } catch (err) {
      alert(err.message || "No se pudo eliminar definitivamente");
    } finally {
      setRemovingForeverId(null);
    }
  };

  if (loading) {
    return <SkeletonGrid variant="doc" count={6} />;
  }

  if (error) {
    return (
      <div className="empty-state">
        <AlertCircle size={40} style={{ color: "#E53E3E", opacity: 0.7 }} />
        <p style={{ color: "#E53E3E" }}>{error}</p>
        <p className="empty-sub">
          Asegurate de que el backend este corriendo.
          <br />
          Ejecuta: <code style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>cd sst-backend && npm start</code>
        </p>
      </div>
    );
  }

  return (
    <>
      {selectMode ? (
        <div className="selection-toolbar">
          <button onClick={toggleSelectAll} className="selection-toggle-btn">
            {selected.length === filtered.length ? <CheckSquare size={16} /> : <Square size={16} />}
            {selected.length === filtered.length ? "Deseleccionar todo" : "Seleccionar todo"}
          </button>
          <span className="selection-count">
            {selected.length} seleccionado{selected.length !== 1 ? "s" : ""}
          </span>
          <div className="selection-spacer" />
          <div className="selection-actions">
            <button className="btn-primary" onClick={sendSelected} disabled={selected.length === 0}>
              <Send size={14} /> Enviar {selected.length > 0 ? `(${selected.length})` : ""} por correo
            </button>
            <button className="btn-secondary" onClick={exitSelectMode}>Cancelar</button>
          </div>
        </div>
      ) : (
        <div className="doc-toolbar" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setSelectMode(true)} className="selection-mode-btn">
            <CheckSquare size={14} /> Seleccion multiple
          </button>
          {isAdmin && (
            <button onClick={openTrashModal} className="selection-mode-btn">
              <Archive size={14} /> Papelera
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} opacity={0.3} />
          <p>No hay documentos en esta carpeta.</p>
          <p className="empty-sub">Sube el primer documento usando el boton superior.</p>
        </div>
      ) : (
        <div className="doc-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((doc, index) => {
              const ext = fileExt(doc);
              const isSelected = selected.includes(doc.id);
              return (
                <motion.div
                  key={doc.id}
                  className="doc-card"
                  custom={index}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  layout
                  style={{ outline: isSelected ? "2px solid var(--primary)" : "none", outlineOffset: 2 }}
                  onClick={() => selectMode && toggleSelect(doc.id)}
                >
                  {selectMode && (
                    <div style={{ position: "absolute", top: 12, right: 12 }}>
                      {isSelected
                        ? <CheckSquare size={18} style={{ color: "var(--primary)" }} />
                        : <Square size={18} style={{ color: "var(--text-muted)" }} />}
                    </div>
                  )}

                  <div className="doc-card-top">
                    {TYPE_ICON[ext] || <FileText size={28} className="doc-icon" />}
                    <div className="doc-badges">
                      <span className={`badge ${TYPE_COLOR[ext] || "badge-pdf"}`}>{ext.toUpperCase()}</span>
                      <span className="badge badge-version">{doc.version}</span>
                    </div>
                  </div>

                  <div className="doc-card-body">
                    <h3 className="doc-name">{doc.name}</h3>
                    <p className="doc-desc">{doc.description}</p>
                    <div className="doc-meta">
                      <span className="meta-cat" title={doc.category}><Tag size={12} /> {doc.category}</span>
                      <span className="meta-date">{formatDate(doc.updated_at || doc.date)}</span>
                    </div>
                    <span className="meta-size">{fmtSize(doc.size)}</span>
                  </div>

                  {!selectMode && (
                    <div className="doc-card-actions">
                      <button className="action-btn" onClick={() => onEdit(doc)} style={{ color: "var(--primary)", borderColor: "#99E8DE" }}>
                        <PenLine size={15} /> Editar
                      </button>
                      <button className="action-btn" onClick={() => sendOne(doc)} title="Enviar por correo" style={{ color: "#6B21A8", borderColor: "#E9D5FF" }}>
                        <Mail size={15} />
                      </button>
                      <button className="action-btn" onClick={() => setEditDoc(doc)} title="Editar datos">
                        <Edit2 size={15} />
                      </button>
                      {canMoveDocuments && (
                        <button className="action-btn" onClick={() => openMoveModal(doc)} title="Mover a otra carpeta">
                          <FolderInput size={15} />
                        </button>
                      )}
                      {doc.form_id && (
                        <button className="action-btn" onClick={() => setResponsesDoc(doc)} title="Ver respuestas del formulario" style={{ color: "#DB2777", borderColor: "#FBCFE8" }}>
                          <BarChart2 size={15} />
                        </button>
                      )}
                      <a className="action-btn" href={api.fileUrl(doc.filename)} download={doc.originalname}>
                        <Download size={15} />
                      </a>
                      {canDelete && (
                        <button className="action-btn danger" onClick={() => handleDeleteDocument(doc)}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {editDoc && <EditModal doc={editDoc} onClose={() => setEditDoc(null)} />}
      {emailDocs && (
        <EmailModal
          docs={emailDocs}
          comunicado={emailComunicado}
          onClose={() => {
            setEmailDocs(null);
            setEmailComunicado(null);
            exitSelectMode();
          }}
        />
      )}
      {responsesDoc && (
        <FormResponsesModal
          formId={responsesDoc.form_id}
          formName={responsesDoc.name}
          companyId={responsesDoc.company_id}
          onClose={() => setResponsesDoc(null)}
          onChanged={fetchDocs}
        />
      )}
      {moveTarget && (
        <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && closeMoveModal()}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div>
                <h2>Mover documento</h2>
                <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 4 }}>{moveTarget.name}</p>
              </div>
              <button className="btn-icon" onClick={closeMoveModal}><X size={18} /></button>
            </div>
            <div className="modal-form">
              <div className="form-row">
                <label>Carpeta destino</label>
                <select className="form-input" value={moveCategory} onChange={(event) => setMoveCategory(event.target.value)}>
                  {CATEGORIES.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
              {moveCategory === moveTarget.category && (
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  El documento ya esta en esa carpeta.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeMoveModal}>Cancelar</button>
              <button className="btn-primary" onClick={handleMoveDocument} disabled={moving || !moveCategory || moveCategory === moveTarget.category}>
                {moving ? <><Loader size={14} className="spin" /> Moviendo...</> : "Mover"}
              </button>
            </div>
          </div>
        </div>
      )}
      {trashOpen && (
        <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && closeTrashModal()}>
          <div className="modal" style={{ maxWidth: 760 }}>
            <div className="modal-header">
              <div>
                <h2>Papelera</h2>
                <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 4 }}>
                  Los archivos se eliminan definitivamente 30 dias despues de enviarlos aqui.
                </p>
              </div>
              <button className="btn-icon" onClick={closeTrashModal}><X size={18} /></button>
            </div>
            <div className="modal-form" style={{ gap: 12 }}>
              {trashLoading ? (
                <div className="empty-state" style={{ minHeight: 180 }}>
                  <Loader size={28} className="spin" opacity={0.4} />
                  <p>Cargando papelera...</p>
                </div>
              ) : trashDocs.length === 0 ? (
                <div className="empty-state" style={{ minHeight: 180 }}>
                  <Archive size={36} opacity={0.3} />
                  <p>La papelera esta vacia.</p>
                </div>
              ) : (
                trashDocs.map((doc) => (
                  <div
                    key={doc.id}
                    style={{
                      display: "flex",
                      gap: 14,
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: "1px solid rgba(0,0,0,0.08)",
                      background: "rgba(0,0,0,0.02)",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.92rem" }}>{doc.name}</div>
                      <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 4 }}>
                        {doc.category} | eliminado {formatDate(doc.deleted_at)} | se borra en {doc.days_until_delete} dia{doc.days_until_delete !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <button className="action-btn" onClick={() => handleRestoreDocument(doc)} disabled={restoringId === doc.id || removingForeverId === doc.id}>
                        {restoringId === doc.id ? <Loader size={14} className="spin" /> : <RotateCcw size={14} />}
                        Restaurar
                      </button>
                      <button className="action-btn danger" onClick={() => handleRemoveForever(doc)} disabled={restoringId === doc.id || removingForeverId === doc.id}>
                        {removingForeverId === doc.id ? <Loader size={14} className="spin" /> : <Trash2 size={14} />}
                        Borrar ya
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {ConfirmUI}
    </>
  );
}
